import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import type { SpaceResource } from '../router/spaces-resources'
import { usablePage } from '../db/page-eligibility'

const visitsSchema = s.array(s.object({ spaceId: s.string(), pageId: s.string(), visitedAt: s.number() }))
const recentPageSchema = s.object({ pageId: s.string(), title: s.string() })
const RECENT_LIMIT = 20

export type RecentVisit = { spaceId: string; pageId: string; visitedAt: number }

function recentKey(userId: string, workspaceId: string) {
  return `flashcards:recent-pages:${JSON.stringify([userId, workspaceId])}`
}

/** Device preference, like the router's last-page pointer; no titles or authored data are stored. */
export function readRecentPages(storage: Storage, userId: string, workspaceId: string): RecentVisit[] {
  const raw = storage.getItem(recentKey(userId, workspaceId))
  if (!raw) return []
  try {
    const visits = visitsSchema.parse(JSON.parse(raw))
    const seen = new Set<string>()
    return visits.filter(visit => {
      const id = JSON.stringify([visit.spaceId, visit.pageId])
      if (seen.has(id) || !Number.isFinite(new Date(visit.visitedAt).getTime())) return false
      seen.add(id)
      return true
    }).slice(0, RECENT_LIMIT)
  } catch { return [] }
}

export function recordRecentPage(storage: Storage, userId: string, workspaceId: string, page: { spaceId: string; id: string }) {
  const previous = readRecentPages(storage, userId, workspaceId)
  const visits = [{ spaceId: page.spaceId, pageId: page.id, visitedAt: Date.now() },
    ...previous.filter(visit => visit.spaceId !== page.spaceId || visit.pageId !== page.id)].slice(0, RECENT_LIMIT)
  storage.setItem(recentKey(userId, workspaceId), JSON.stringify(visits))
}

/** One bounded live read per physical resource; stored references confer no access. */
export function recentPagesOptions(resource: SpaceResource, visits: RecentVisit[]) {
  const { db } = resource
  const ids = visits.filter(visit => visit.spaceId === resource.space.id).map(visit => visit.pageId)
  return queryOptions(db.query`SELECT ${db.pages.id} AS pageId, ${db.pages.title} FROM ${db.pages}
    WHERE ${db.pages.id.inArray(ids)} AND ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)}`,
  recentPageSchema, { staleTime: Infinity })
}
